import React, { useEffect, useState } from "react";
import { Loader2, ClipboardList, Plus, X, Pill } from "lucide-react";
import { getIntake, mapIntakeAnswers } from "@/lib/telehealth";

const catColor = (c) => ({
  reason: "#1E40AF", medications: "#5B21B6", allergies: "#9A5A00",
  history: "#0E7490", screening: "#166534",
}[c] || "#6C6C69");

// Read-only pre-visit intake: each question with the patient's submitted answer.
// `token` is the appointment's patient_link_token.
export function IntakeView({ token, dense }) {
  const [state, setState] = useState({ loading: true, questions: [], answers: {}, completed: false, error: false });
  useEffect(() => {
    let alive = true;
    if (!token) { setState({ loading: false, questions: [], answers: {}, completed: false, error: false }); return undefined; }
    setState((s) => ({ ...s, loading: true }));
    getIntake(token)
      .then((d) => { if (alive) setState({ loading: false, questions: d.questions || [], answers: mapIntakeAnswers(d.answers), completed: !!d.completed, error: false }); })
      .catch(() => { if (alive) setState({ loading: false, questions: [], answers: {}, completed: false, error: true }); });
    return () => { alive = false; };
  }, [token]);

  if (state.loading) return <div className="flex items-center justify-center py-10 text-[12.5px]" style={{ color: "#9B9B97" }}><Loader2 size={15} className="animate-spin mr-2" /> Loading intake…</div>;
  if (state.error || state.questions.length === 0) return <div className="text-center py-10 text-[12.5px]" style={{ color: "#9B9B97" }}><ClipboardList size={18} className="mx-auto mb-2" style={{ opacity: 0.5 }} />No intake form for this visit.</div>;

  const answered = Object.values(state.answers).filter((v) => String(v).trim()).length;
  const total = state.questions.length;

  return (
    <div className={dense ? "space-y-2.5" : "p-3 space-y-2.5"} data-testid="tele-intake">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[.04em]" style={{ color: "#8A8A86" }}>Pre-visit intake</span>
        <span className="flex-1" />
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: state.completed ? "#E7F7EC" : "#FFF4E0", color: state.completed ? "#166534" : "#9A5A00" }} data-testid="tele-intake-state">
          {state.completed ? "Submitted" : `Pending · ${answered}/${total}`}
        </span>
      </div>
      {state.questions.map((q, i) => {
        const ans = state.answers[String(q.id)] || "";
        return (
          <div key={q.id || i} className="rounded-[11px] p-3" style={{ background: "#fff", border: "1px solid #E6E6E4" }}>
            <div className="flex items-start gap-2">
              <span className="mt-0.5 w-[4px] self-stretch rounded-full flex-none" style={{ background: catColor(q.category) }} />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold" style={{ color: "#141412" }}>{q.question}</div>
                {ans.trim()
                  ? <div className="text-[13px] mt-1 whitespace-pre-wrap" style={{ color: "#44443F" }}>{ans}</div>
                  : <div className="text-[12.5px] mt-1 italic" style={{ color: "#9B9B97" }}>Not answered</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// In-session medication staging. Shows current chart meds and a staging list the
// provider builds for THIS visit only — carried into the encounter note, not saved
// to a separate telehealth record. Lives in the live-visit screen; clears when it closes.
export function MedStaging({ currentMeds = [], staged, setStaged }) {
  const [text, setText] = useState("");
  const add = () => { const t = text.trim(); if (!t) return; setStaged((s) => [...s, { id: Date.now() + Math.random(), name: t }]); setText(""); };
  const stageCurrent = (name) => { if (staged.some((m) => m.name === name)) return; setStaged((s) => [...s, { id: Date.now() + Math.random(), name }]); };
  const remove = (id) => setStaged((s) => s.filter((m) => m.id !== id));
  return (
    <div className="p-3 space-y-4" data-testid="tele-meds">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1.5" style={{ color: "#8A8A86" }}>Current medications</div>
        {currentMeds.length === 0 ? <div className="text-[12.5px]" style={{ color: "#9B9B97" }}>None recorded on the chart.</div>
          : <div className="space-y-1.5">{currentMeds.map((m, i) => (
            <div key={i} className="flex items-center gap-2 rounded-[9px] px-2.5 py-2" style={{ background: "#fff", border: "1px solid #E6E6E4" }}>
              <Pill size={13} style={{ color: "#5B21B6" }} />
              <span className="text-[12.5px] flex-1 min-w-0 truncate" style={{ color: "#44443F" }}>{m}</span>
              <button onClick={() => stageCurrent(m)} data-testid={`tele-med-stage-current-${i}`} className="text-[11px] font-semibold px-2 py-1 rounded-[7px]" style={{ border: "1px solid #E4E4E7", color: "#166534" }}>Continue</button>
            </div>
          ))}</div>}
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[.04em]" style={{ color: "#8A8A86" }}>Staged for this visit</span>
          <span className="flex-1" />
          {staged.length > 0 ? <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: "#5B21B6" }}>{staged.length}</span> : null}
        </div>
        <div className="flex items-center gap-2 mb-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a medication to continue or prescribe…"
            data-testid="tele-med-input" className="flex-1 h-[34px] px-3 rounded-[9px] text-[12.5px] outline-none" style={{ border: "1px solid #E4E4E7" }} />
          <button onClick={add} disabled={!text.trim()} data-testid="tele-med-add" className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center text-white disabled:opacity-40" style={{ background: "#5B21B6" }}><Plus size={16} /></button>
        </div>
        {staged.length === 0 ? <div className="text-[12.5px]" style={{ color: "#9B9B97" }}>Nothing staged yet.</div>
          : <div className="space-y-1.5">{staged.map((m) => (
            <div key={m.id} className="flex items-center gap-2 rounded-[9px] px-2.5 py-2" style={{ background: "#F7F5FC", border: "1px solid #E4DCF4" }} data-testid={`tele-med-staged-${m.id}`}>
              <span className="text-[12.5px] flex-1 min-w-0 truncate font-medium" style={{ color: "#3F2E6E" }}>{m.name}</span>
              <button onClick={() => remove(m.id)} aria-label="Remove" className="w-[24px] h-[24px] rounded-[6px] flex items-center justify-center" style={{ color: "#8A7BB0" }}><X size={13} /></button>
            </div>
          ))}</div>}
        <div className="text-[11px] mt-2.5 rounded-[9px] px-2.5 py-2" style={{ background: "#FBFBFA", border: "1px dashed #DDDDDA", color: "#8A8A86" }}>
          Staged meds are for this visit only — they're added to the encounter note and cleared when the visit ends. Not saved to a separate telehealth record.
        </div>
      </div>
    </div>
  );
}
